import React, { useState, useMemo } from "react";
import { App as AntdApp, Button, Card, Collapse, Empty, Input, Space, Tag, Typography } from "antd";
import { DeleteOutlined, RollbackOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";

import api from "@/api/axios.js";
import { useSSE } from "@modules/realtime/hooks/useSSE.js";
import { useAuth } from "@modules/auth/AuthContext.jsx";

import CreateFlyerWizard from "../components/CreateFlyerWizard.jsx";
import FlyersTable from "../components/FlyersTable.jsx";
import ManageFlyerModal from "../components/ManageFlyerModal.jsx";
import FlyerFiltersBar, { EMPTY_FLYER_FILTERS } from "../components/FlyerFiltersBar.jsx";

import "./VolantiniPage.css";

const { Title, Text } = Typography;

const BRAND = {
	primary: "#E31E24",
	dark: "#2D2D2D",
	light: "#f7f7f7"
};

// La ricerca parte quando l'utente smette di scrivere: con centinaia di volantini
// ricalcolare e ridisegnare a ogni tasto bloccava la pagina.
const SEARCH_DEBOUNCE_MS = 300;
// Con ricerca o filtri attivi si aprono al massimo queste promo (le più recenti);
// le altre restano chiuse col numero di risultati, a un clic di distanza.
const SEARCH_OPEN_GROUPS_LIMIT = 10;
// Gli aggiornamenti in tempo reale (una card creata dall'AI = un evento) si applicano
// tutti insieme al massimo una volta in questo intervallo, invece di uno alla volta.
const SSE_FLUSH_MS = 1000;

export default function VolantiniPage({ openOnMount = false, onOpened, onManage, mode = "active" }) {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { message } = AntdApp.useApp();
	const { user } = useAuth();
	const [manage, setManage] = React.useState({ open: false, id: null, name: "" });

	const isTrash = mode === "trash";

	const [openWizard, setOpenWizard] = useState(!isTrash && (!!searchParams.get("new") || openOnMount));

	const [flyers, setFlyers] = useState([]);
	const [loadingTable, setLoadingTable] = useState(false);
	const [q, setQ] = useState("");
	const [searchQ, setSearchQ] = useState("");
	const [trashedCount, setTrashedCount] = useState(0);

	// `q` segue il campo di ricerca, `searchQ` è il testo davvero cercato. Svuotando il
	// campo (anche con la X) la lista torna completa subito, senza attesa.
	React.useEffect(() => {
		if (!q.trim()) {
			setSearchQ("");
			return undefined;
		}
		const timer = window.setTimeout(() => setSearchQ(q), SEARCH_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [q]);

	// Quanti volantini chiedere al server. Il default dell'endpoint è 50: senza
	// un limite esplicito la pagina ne nasconderebbe silenziosamente centinaia
	// (in produzione 485 attivi). Gli altri chiamanti di /api/flyers passano già
	// un limite proprio. Non è paginazione: il raggruppamento per promo e
	// l'espansione delle varianti lavorano sull'insieme completo lato client.
	const FLYERS_FETCH_LIMIT = 2000;

	// La query string è l'unica fonte di verità dei filtri: ricaricare la pagina
	// o incollare il link a un collega riproduce la stessa vista.
	const filters = useMemo(() => ({
		cooperativaIds: searchParams.getAll("cliente"),
		canaleIds: searchParams.getAll("canale"),
		promoIds: searchParams.getAll("promo")
	}), [searchParams]);

	const hasFilters = filters.cooperativaIds.length > 0
		|| filters.canaleIds.length > 0
		|| filters.promoIds.length > 0;

	const handleFiltersChange = (next) => {
		setSearchParams((prev) => {
			const params = new URLSearchParams(prev);
			params.delete("cliente");
			params.delete("canale");
			params.delete("promo");
			next.cooperativaIds.forEach(v => params.append("cliente", v));
			next.canaleIds.forEach(v => params.append("canale", v));
			next.promoIds.forEach(v => params.append("promo", v));
			return params;
		}, { replace: true });
	};

	React.useEffect(() => {
		if (openOnMount && !isTrash) {
			setOpenWizard(true);
			onOpened?.();
		}
	}, [openOnMount, onOpened, isTrash]);

	// Ogni richiesta porta un numero di sequenza: cambiando filtro in fretta, la
	// risposta di una richiesta superata non deve sovrascrivere quella corrente.
	// È la guardia che permette di lasciare le tende attive durante il caricamento.
	const fetchSeqRef = React.useRef(0);

	const fetchFlyers = async () => {
		const seq = ++fetchSeqRef.current;
		setLoadingTable(true);
		try {
			const url = isTrash ? "/api/flyers/trash" : "/api/flyers";
			// Axios serializza gli array come `cliente[]=a&cliente[]=b` e il parser
			// `qs` di Express li riconverte in array: nessun serializzatore custom.
			const { data } = await api.get(url, {
				params: {
					limit: FLYERS_FETCH_LIMIT,
					cooperativaId: filters.cooperativaIds,
					canaleId: filters.canaleIds,
					promoId: filters.promoIds
				}
			});
			if (seq !== fetchSeqRef.current) return;
			const items = data?.items || data || [];
			setFlyers(items);
			setTrashedCount(Number(data?.trashedCount || 0));
		}
		catch (error) {
			if (seq !== fetchSeqRef.current) return;
			setFlyers([]);
			setTrashedCount(0);
			message.error(error?.response?.data?.error || "Impossibile caricare i volantini");
		}
		finally {
			if (seq === fetchSeqRef.current) setLoadingTable(false);
		}
	};

	// Firma stabile: `searchParams` cambia identità anche per parametri estranei
	// ai filtri (per esempio `?new=1` del wizard), che non devono far ricaricare.
	const filtersKey = `${filters.cooperativaIds.join(",")}|${filters.canaleIds.join(",")}|${filters.promoIds.join(",")}`;

	React.useEffect(() => {
		fetchFlyers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTrash, filtersKey]);

	// Ogni evento accoda la modifica del suo volantino; la coda si applica in un solo
	// aggiornamento ogni SSE_FLUSH_MS, nell'ordine di arrivo (i conteggi restano esatti).
	const sseQueueRef = React.useRef([]);
	const sseTimerRef = React.useRef(null);

	const queueFlyerUpdate = React.useCallback((flyerId, update) => {
		if (!flyerId) return;
		sseQueueRef.current.push({ flyerId: String(flyerId), update });
		if (sseTimerRef.current) return;
		sseTimerRef.current = window.setTimeout(() => {
			sseTimerRef.current = null;
			const queue = sseQueueRef.current;
			sseQueueRef.current = [];
			if (!queue.length) return;
			setFlyers((prev) => {
				const byId = new Map(prev.map((f) => [String(f._id || f.id), f]));
				for (const { flyerId: id, update: apply } of queue) {
					const current = byId.get(id);
					if (current) byId.set(id, apply(current));
				}
				return prev.map((f) => byId.get(String(f._id || f.id)) || f);
			});
		}, SSE_FLUSH_MS);
	}, []);

	React.useEffect(() => () => {
		if (sseTimerRef.current) window.clearTimeout(sseTimerRef.current);
	}, []);

	useSSE("flyers", {
		"flyer.status.changed": (p) => {
			queueFlyerUpdate(p?.flyerId, (f) => {
				const updates = { status: p.status };
				if (p.cardsCount !== undefined) updates.cardsCount = p.cardsCount;
				return { ...f, ...updates };
			});
		},
		"flyer.card.created": (p) => {
			queueFlyerUpdate(p?.flyerId, (f) => ({ ...f, cardsCount: (f.cardsCount || 0) + 1 }));
		},
		"flyer.card.deleted": (p) => {
			queueFlyerUpdate(p?.flyerId, (f) => ({ ...f, cardsCount: Math.max(0, (f.cardsCount || 1) - 1) }));
		},
		"flyer.refs.updated": (p) => {
			// Senza numero l'evento dice solo "le foto sono cambiate": si tiene il conteggio attuale.
			if (typeof p?.count !== "number") return;
			queueFlyerUpdate(p.flyerId, (f) => ({ ...f, refsCount: p.count }));
		},
		"flyer.ocr.progress": (p) => {
			queueFlyerUpdate(p?.flyerId, (f) => ({ ...f, processingProgress: p.percent }));
		}
	});

	// Filtro ricerca: matcha sul nome del volantino, del padre (per le varianti)
	// o della promo (così la ricerca per promo mostra l'intero gruppo).
	const filteredFlyers = useMemo(() => {
		const qLow = searchQ.trim().toLowerCase();
		if (!qLow) return flyers;
		return flyers.filter(f =>
			(f.name || "").toLowerCase().includes(qLow)
			|| (f.parentFlyerName || "").toLowerCase().includes(qLow)
			|| (f.promoName || "").toLowerCase().includes(qLow)
		);
	}, [flyers, searchQ]);

	// Raggruppamento per promo: ogni promo è una sezione con sotto i suoi
	// volantini e varianti (piatti). I volantini senza promo finiscono in coda.
	const NO_PROMO_KEY = "__no_promo__";
	const promoGroups = useMemo(() => {
		const map = new Map();
		for (const f of filteredFlyers) {
			const key = f.promoId ? String(f.promoId) : NO_PROMO_KEY;
			if (!map.has(key)) {
				map.set(key, {
					key,
					promoId: f.promoId || null,
					name: f.promoId ? (f.promoName || "Promo senza nome") : "Senza promo",
					isLegacy: !!f.promoIsLegacy,
					flyers: [],
					latest: 0
				});
			}
			const group = map.get(key);
			group.flyers.push(f);
			const ts = new Date(f.updatedAt || f.createdAt || 0).getTime();
			if (ts > group.latest) group.latest = ts;
		}
		return [...map.values()].sort((a, b) => {
			// "Senza promo" sempre ultimo, poi le Legacy, poi per attività recente
			const aNone = a.key === NO_PROMO_KEY;
			const bNone = b.key === NO_PROMO_KEY;
			if (aNone !== bNone) return aNone ? 1 : -1;
			if (a.isLegacy !== b.isLegacy) return a.isLegacy ? 1 : -1;
			return b.latest - a.latest;
		});
	}, [filteredFlyers]);

	// Apertura sezioni controllata con persistenza:
	// - ricerca o filtri attivi → apre le prime SEARCH_OPEN_GROUPS_LIMIT promo trovate
	//   (le più recenti), senza toccare la preferenza salvata
	// - altrimenti → ripristina lo stato salvato in localStorage; in assenza, apre tutto
	//   ma parte collassato se le promo sono molte (oltre la soglia)
	// La preferenza è per utente: sullo stesso browser chi entra dopo non eredita le
	// promo aperte dal collega.
	const GROUPS_STORAGE_KEY = `vp.openPromoGroups:${user?.id || "anon"}`;
	const GROUPS_COLLAPSE_THRESHOLD = 8;
	const groupKeysSignature = promoGroups.map(g => g.key).join("|");
	const [openGroups, setOpenGroups] = useState([]);

	React.useEffect(() => {
		const allKeys = promoGroups.map(g => g.key);
		if (searchQ.trim() || hasFilters) {
			setOpenGroups(allKeys.slice(0, SEARCH_OPEN_GROUPS_LIMIT));
			return;
		}
		let saved = null;
		try {
			const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
			if (raw) saved = JSON.parse(raw);
		}
		catch { /* localStorage non disponibile */ }
		if (Array.isArray(saved)) {
			setOpenGroups(saved.filter(k => allKeys.includes(k)));
		}
		else {
			setOpenGroups(allKeys.length > GROUPS_COLLAPSE_THRESHOLD ? [] : allKeys);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groupKeysSignature, searchQ, hasFilters, GROUPS_STORAGE_KEY]);

	const handleGroupsChange = (keys) => {
		const arr = Array.isArray(keys) ? keys : [keys];
		setOpenGroups(arr);
		// Persisto solo le scelte manuali fuori dalla ricerca, per non salvare
		// l'apertura forzata dei risultati.
		if (!searchQ.trim() && !hasFilters) {
			try { localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(arr)); }
			catch { /* localStorage non disponibile */ }
		}
	};

	const goToTrash = () => navigate("/flyers/trash");
	const goToMain = () => navigate("/flyers");

	// Il conteggio è la risposta visibile al troncamento silenzioso: dice sempre
	// quanti volantini si stanno guardando, e quanti ne sono stati esclusi dalla
	// ricerca testuale (che agisce in memoria, dopo i filtri del server).
	const countLabel = searchQ.trim()
		? `${filteredFlyers.length} di ${flyers.length} volantini`
		: `${flyers.length} volantini`;

	const showEmptyState = !loadingTable && filteredFlyers.length === 0 && (hasFilters || !!searchQ.trim());

	return (
		<>
			<div className="vp-head">
				<div>
					<Title level={3} className="vp-h1">{isTrash ? "Cestino" : "Volantini"}</Title>
					<Text style={{ color: BRAND.primary }}>
						{isTrash ? "Volantini eliminati, ripristinabili o eliminabili in modo definitivo" : "Gestisci e crea nuovi volantini"}
					</Text>
					<div className="vp-sub" style={{ marginTop: 4 }}>{countLabel}</div>
				</div>
				<Space>
					<Input.Search
						allowClear
						placeholder="Cerca per nome…"
						value={q}
						onChange={(e) => setQ(e.target.value)}
						onSearch={() => {}}
						style={{ width: 280 }}
					/>
					{isTrash ? (
						<Button icon={<RollbackOutlined />} onClick={goToMain}>
							Torna ai volantini
						</Button>
					) : (
						<>
							<Button icon={<DeleteOutlined />} onClick={goToTrash}>
								Cestino ({trashedCount})
							</Button>
							<Button type="primary" size="large" onClick={() => setOpenWizard(true)}>
								Nuovo volantino
							</Button>
						</>
					)}
				</Space>
			</div>

			<FlyerFiltersBar
				value={filters}
				onChange={handleFiltersChange}
			/>

			{showEmptyState && (
				<Card className="wow-table-card card-elev">
					<Empty
						description={hasFilters
							? "Nessun volantino per i filtri selezionati"
							: "Nessun volantino corrisponde alla ricerca"}
					>
						{hasFilters && (
							<Button onClick={() => handleFiltersChange(EMPTY_FLYER_FILTERS)}>
								Azzera filtri
							</Button>
						)}
					</Empty>
				</Card>
			)}

			{!showEmptyState && isTrash && (
				<FlyersTable
					flyers={filteredFlyers}
					loading={loadingTable}
					isTrash
					flat={false}
					onManage={onManage}
					onRefresh={fetchFlyers}
				/>
			)}

			{!showEmptyState && !isTrash && (
				<Card className="wow-table-card card-elev">
					<Collapse
						destroyOnHidden
						activeKey={openGroups}
						onChange={handleGroupsChange}
						items={promoGroups.map((group) => ({
							key: group.key,
							label: (
								<div className="vp-group-head">
									<Space size={8} wrap>
										<b>{group.name}</b>
										{group.isLegacy && <Tag>Legacy</Tag>}
										{!group.promoId && <Tag color="default">Orfani</Tag>}
										<Tag color="blue">{group.flyers.length}</Tag>
									</Space>
									{group.promoId && (
										<Button
											type="link"
											size="small"
											onClick={(e) => { e.stopPropagation(); navigate(`/promos/${group.promoId}`); }}
										>
											Apri promo <ArrowRightOutlined />
										</Button>
									)}
								</div>
							),
							children: (
								<FlyersTable
									embedded
									flyers={group.flyers}
									loading={loadingTable}
									flat
									onManage={onManage}
									onRefresh={fetchFlyers}
								/>
							)
						}))}
					/>
				</Card>
			)}

			{!isTrash && (
				<CreateFlyerWizard
					open={openWizard}
					onClose={() => setOpenWizard(false)}
					onFlyerCreated={() => fetchFlyers()}
				/>
			)}

			<ManageFlyerModal
				open={manage.open}
				flyerId={manage.id}
				flyerName={manage.name}
				onClose={() => setManage({ open: false, id: null, name: "" })}
			/>
		</>
	);
}
